class MagazinSlider {
  #el: HTMLElement;
  #track!: HTMLElement;
  #slides: HTMLElement[] = [];
  #dotsContainer: HTMLElement | null = null;
  #currentIndex = 0;

  constructor(el: HTMLElement) {
    this.#el = el;

    const blockId = el.dataset.blockId;
    if (!blockId) return;

    const track = el.querySelector<HTMLElement>(`#MagazinTrack-${blockId}`);
    if (!track) return;

    this.#track = track;
    this.#dotsContainer = el.querySelector<HTMLElement>(`#MagazinDots-${blockId}`);

    this.#init();
  }

  #init(): void {
    this.#slides = Array.from(this.#track.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    );

    if (this.#slides.length === 0) return;

    this.#buildDots();
    this.#bindArrows();
    this.#bindSlideLinks();
    this.#bindEvents();
    this.#update();
  }

  #resolveHref(): string | null {
    const fromSetting = this.#el.dataset.link?.trim();
    if (fromSetting) return fromSetting;

    // ponytail: reuse last-slide CTA so existing theme content links without reconfig
    for (let i = this.#slides.length - 1; i >= 0; i--) {
      const anchor = this.#slides[i].querySelector<HTMLAnchorElement>('a[href]');
      const href = anchor?.getAttribute('href')?.trim();
      if (href) return href;
    }

    return null;
  }

  #bindSlideLinks(): void {
    const href = this.#resolveHref();
    if (!href) return;

    this.#el.classList.add('magazin-slider--linked');

    for (const slide of this.#slides) {
      slide.addEventListener('click', (e) => {
        const target = e.target as Element | null;
        if (target?.closest('a, button, .magazin-slider__next, .magazin-slider__dot')) return;
        window.location.href = href;
      });
    }
  }

  #buildDots(): void {
    if (!this.#dotsContainer) return;

    this.#dotsContainer.innerHTML = '';

    for (let i = 0; i < this.#slides.length; i++) {
      const dot = document.createElement('button');
      dot.className = 'magazin-slider__dot';
      dot.setAttribute('aria-label', `Slide ${i + 1}`);
      dot.dataset.index = String(i);

      dot.addEventListener('click', () => {
        this.#goTo(i);
      });

      this.#dotsContainer.appendChild(dot);
    }
  }

  #bindArrows(): void {
    for (let i = 0; i < this.#slides.length; i++) {
      const arrow = this.#slides[i].querySelector<HTMLButtonElement>('.magazin-slider__next');
      if (!arrow) continue;

      arrow.addEventListener('click', (e) => {
        e.preventDefault();
        this.#goTo(i + 1);
      });
    }
  }

  #update(): void {
    const isLast = this.#currentIndex === this.#slides.length - 1;

    // Hide arrow on last slide
    for (let i = 0; i < this.#slides.length; i++) {
      const arrow = this.#slides[i].querySelector<HTMLElement>('.magazin-slider__next');
      if (!arrow) continue;
      arrow.style.display = i === this.#slides.length - 1 && isLast ? 'none' : '';
    }

    if (!this.#dotsContainer) return;

    const dots = this.#dotsContainer.querySelectorAll<HTMLElement>('.magazin-slider__dot');

    dots.forEach((dot, i) => {
      dot.classList.toggle('magazin-slider__dot--active', i === this.#currentIndex);
    });
  }

  #goTo(index: number): void {
    this.#currentIndex = Math.max(0, Math.min(index, this.#slides.length - 1));
    this.#track.scrollTo({ left: this.#track.clientWidth * this.#currentIndex, behavior: 'smooth' });
    this.#update();
  }

  #bindEvents(): void {
    // Sync index from scroll position (touch swipe / trackpad)
    this.#track.addEventListener(
      'scrollend',
      () => {
        const idx = Math.round(this.#track.scrollLeft / this.#track.clientWidth);

        if (idx !== this.#currentIndex) {
          this.#currentIndex = idx;
          this.#update();
        }
      },
      { passive: true },
    );

    // Fallback for browsers without scrollend
    let scrollTimer: ReturnType<typeof setTimeout>;
    this.#track.addEventListener(
      'scroll',
      () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const idx = Math.round(this.#track.scrollLeft / this.#track.clientWidth);

          if (idx !== this.#currentIndex) {
            this.#currentIndex = idx;
            this.#update();
          }
        }, 100);
      },
      { passive: true },
    );
  }
}

export function initMagazinSlider(root: ParentNode = document): void {
  const sliders = Array.from(root.querySelectorAll<HTMLElement>('[data-magazin-slider]:not([data-magazin-init])'));

  for (const el of sliders) {
    el.dataset.magazinInit = 'true';
    new MagazinSlider(el);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initMagazinSlider());
} else {
  initMagazinSlider();
}

document.addEventListener('shopify:section:load', (e: Event) => {
  const target = (e as CustomEvent).target as ParentNode | null;
  initMagazinSlider(target ?? document);
});
